import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useState } from "react";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { DispatchResponse } from "~/shared/schemas/shipment-management-schema.ts";
import { dispatchResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { ApiError } from "~/shared/types/api-types.ts";

export type DispatchError = {
	attemptedAt: Date;
	message: string;
};

export type DispatchRequest = {
	poNumber: string;
	customerId?: number;
};

const DISPATCH_FAILED_MESSAGE = "Failed to dispatch. Please try again.";

const getDispatchFailureMessage = (result: DispatchResponse): string | null => {
	const messages = [
		...(result.validationErrors ?? []),
		...(result.errorMessage ? [result.errorMessage] : []),
	];
	return messages.length > 0 ? messages.join(" ") : null;
};

/**
 * A failed dispatch returns 400 with the dispatch result body
 * (`validationErrors` / `errorMessage`), which the shared `extractApiError`
 * does not read. Returns null for any other shape (e.g. ProblemDetails) so the
 * fetch client's default extraction still applies.
 */
export const extractDispatchError = async (
	error: unknown,
): Promise<ApiError | null> => {
	if (!(error instanceof HTTPError)) {
		return null;
	}

	try {
		const body: unknown = await error.response.clone().json();
		const parsed = dispatchResponseSchema.safeParse(body);
		if (!parsed.success) {
			return null;
		}

		const message = getDispatchFailureMessage(parsed.data);
		if (!message) {
			return null;
		}

		return {
			success: false,
			error: message,
			details: body,
			status: error.response.status,
		};
	} catch {
		return null;
	}
};

export const postApiDispatch = async ({
	poNumber,
	customerId,
}: DispatchRequest) => {
	// customerId attributes the dispatch-failure exception to the shipment's
	// customer so it is returned with the shipment.
	const resp = await postAndValidate(
		apiRoutes.POSTDISPATCH,
		{ poNumber, customerId },
		dispatchResponseSchema,
		{ onError: extractDispatchError },
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostApiDispatch = () => {
	const queryClient = useQueryClient();
	const { showSuccess } = useShipmentNotifications();
	const [dispatchError, setDispatchError] = useState<DispatchError | null>(
		null,
	);
	const clearDispatchError = () => setDispatchError(null);
	// A failed dispatch adds an exception flag; refetch so its banner shows
	// without a reload. Not awaited so the error displays right away.
	const refetchShipmentAfterFailure = () => {
		void queryClient.invalidateQueries({ queryKey: ["shipment"] });
	};

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		DispatchResponse,
		Error,
		DispatchRequest
	>({
		mutationFn: (request: DispatchRequest) => {
			if (!request.poNumber) {
				throw new Error("PO Number is required");
			}
			return postApiDispatch(request);
		},
		onSuccess: async (result) => {
			if (!result.success) {
				setDispatchError({
					attemptedAt: new Date(),
					message: getDispatchFailureMessage(result) ?? DISPATCH_FAILED_MESSAGE,
				});
				refetchShipmentAfterFailure();
				return;
			}

			// Awaiting the refetch keeps the mutation pending (and the spinner up)
			// until the shipment reflects the new state; toast once it's done.
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Shipment dispatched.");
		},
		onError: (err) => {
			setDispatchError({
				attemptedAt: new Date(),
				message: err instanceof Error ? err.message : DISPATCH_FAILED_MESSAGE,
			});
			refetchShipmentAfterFailure();
		},
	});

	return {
		apiDispatch: mutate,
		apiDispatchAsync: mutateAsync,
		isSaving: isPending,
		error,
		data,
		resetError: reset,
		dispatchError,
		clearDispatchError,
	};
};
